import { Clip } from '../../types/clip-types';
import { ClipDeletes, CompositeSegment } from '../../types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { Word } from '../../types/transcript-types';
import { approxEquals, approxLT } from '../../utils/compareUtils';

// Below this frame-width, a layout change is treated as noise (e.g. epsilon gap before the first real word) and merged into a neighbor instead of being shown.
export const MIN_LAYOUT_CHANGE_DURATION_FRAMES = 1;

// Drops negligible-duration layout changes: a leading one is merged forward (adopts the next change's properties), others are merged into the previous one. Frame coverage is preserved, only reassigned.
export const mergeNegligibleLayoutChanges = <T extends { fromFrame: number; toFrame: number }>(
  layoutChanges: T[],
  minFrames: number = MIN_LAYOUT_CHANGE_DURATION_FRAMES
): T[] => {
  if (layoutChanges.length <= 1) {
    // New array: callers may do `arr.length = 0; arr.push(...cleaned)` in place.
    return [...layoutChanges];
  }

  const result: T[] = [];

  for (const change of layoutChanges) {
    const duration = change.toFrame - change.fromFrame;
    const previous = result[result.length - 1];
    const previousDuration = previous ? previous.toFrame - previous.fromFrame : undefined;

    if (previous && previousDuration !== undefined && previousDuration < minFrames) {
      // Previous was negligible: adopt this change's properties, keep its fromFrame.
      result[result.length - 1] = { ...change, fromFrame: previous.fromFrame };
      continue;
    }

    if (duration < minFrames && previous) {
      // This change is negligible: ignore it by extending the previous one over it.
      result[result.length - 1] = { ...previous, toFrame: change.toFrame };
      continue;
    }

    result.push(change);
  }

  return result;
};

// Drops negligible-duration play ranges (e.g. zero or epsilon-wide ranges from near-duplicate frame points), which Remotion's <Series.Sequence> rejects or plays as junk.
// Play ranges are in source frames and deletes leave gaps between them, so a negligible range is only merged into a neighbor it is contiguous with; this never pulls deleted footage back in.
// A negligible range with no contiguous neighbor is dropped when it has no positive duration.
export const mergeNegligiblePlayRanges = <T extends { fromFrame: number; toFrame: number }>(
  playRanges: T[],
  minFrames: number = MIN_LAYOUT_CHANGE_DURATION_FRAMES
): T[] => {
  const result: T[] = [];

  for (const range of playRanges) {
    const previous = result[result.length - 1];
    const isContiguousWithPrevious = previous !== undefined && approxEquals(previous.toFrame, range.fromFrame);

    if (
      isContiguousWithPrevious &&
      (previous.toFrame - previous.fromFrame < minFrames || range.toFrame - range.fromFrame < minFrames)
    ) {
      result[result.length - 1] = { ...previous, toFrame: range.toFrame };
      continue;
    }

    result.push(range);
  }

  return result.filter(range => range.toFrame - range.fromFrame > 0 && !approxEquals(range.toFrame, range.fromFrame));
};

export const getFlattenedDeletes = ({ clipDeletes, fps }: { clipDeletes: ClipDeletes; fps: number }) => {
  if (clipDeletes === undefined) {
    return [];
  }
  return Object.values(clipDeletes)
    .map(data => ({
      fromFrame: data.bounds[0] * fps,
      toFrame: data.bounds[1] * fps
    }))
    .filter(data => data.toFrame !== data.fromFrame)
    .sort((a, b) => a.fromFrame - b.fromFrame);
}; // This is required to adjust the frame of the main segment to account for the deletes

export const getAdjustedFrame = (frame: number, deletes: ReturnType<typeof getFlattenedDeletes>, start: number) => {
  let adjustment = 0;

  for (const deleteData of deletes) {
    if (deleteData.fromFrame >= frame) {
      break;
    }
    if (deleteData.toFrame <= frame) {
      adjustment += deleteData.toFrame - deleteData.fromFrame;
    }
  }

  return frame - adjustment - start;
}; // This is required to get the available ranges after the deletes

export const getAvailableRanges = (
  totalRange: [number, number],
  deletedRanges: ReturnType<typeof getFlattenedDeletes>
) => {
  // Extract the start and end of the total range
  const [totalStart, totalEnd] = totalRange;

  // Sort the deleted ranges by their start value
  deletedRanges.sort((a, b) => a.fromFrame - b.fromFrame);

  const remainingRanges: [number, number][] = [];

  // Track the end of the last deleted range
  let lastEnd = totalStart;

  // Loop through the deleted ranges and find gaps
  for (let i = 0; i < deletedRanges.length; i++) {
    const { fromFrame, toFrame } = deletedRanges[i];

    // If there is a gap before the current deleted range
    if (fromFrame > lastEnd) {
      remainingRanges.push([lastEnd, fromFrame]);
    }

    // Update the last end point to the end of the current deleted range
    lastEnd = Math.max(lastEnd, toFrame);
  }

  // If there is any remaining range after the last deleted range
  if (lastEnd < totalEnd) {
    remainingRanges.push([lastEnd, totalEnd]);
  }

  return remainingRanges;
};
export function getSegmentTranslatedCaptions(newCompositeSegment: CompositeSegment, clip: Clip): Word[] {
  if (!newCompositeSegment.segment_metadata) {
    return [];
  }
  const segStart = newCompositeSegment.segment_metadata.start;
  const segEnd = newCompositeSegment.segment_metadata.end;
  const excludes = newCompositeSegment.segment_metadata.excludes || [];
  let captionsIndex = 0; // index setup is needed for selection since translated transcript is

  // pulled from segment_metadata.captions and not clip.asset_metadata.captions
  return (clip.asset_metadata.captions || [])
    .filter(
      caption =>
        typeof caption.start_time === 'number' &&
        typeof caption.end_time === 'number' &&
        caption.start_time >= segStart &&
        caption.end_time <= segEnd
    ) // get captions for segment time boundaries
    .filter(caption => {
      const wordEdit = newCompositeSegment.segment_metadata?.edits?.[caption.start_time];
      const excluded = excludes?.find(ex => caption.start_time >= ex[0] && caption.end_time <= ex[1]);
      return !excluded || !!wordEdit?.word;
    }) // hide deleted words
    .map(caption => {
      const wordEdit = newCompositeSegment.segment_metadata?.edits?.[caption.start_time];
      return {
        ...caption,
        processed_start_time: caption.start_time - segStart - getExcludesAdjustedTime(caption.start_time, excludes),
        processed_end_time: caption.end_time - segStart - getExcludesAdjustedTime(caption.end_time, excludes),
        content: wordEdit?.word ? wordEdit.word : caption.content,
        index: captionsIndex++
      };
    });
}

function getExcludesAdjustedTime(time: number, excludes: [number, number][]): number {
  return excludes
    .filter(e => approxLT(e[0], time))
    .map(e => Math.min(e[1], time) - e[0])
    .reduce((a, b) => a + b, 0);
}
